import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { GridBaseColDef } from '@mui/x-data-grid/internals';
import {
  GridActionsCellItem,
  GridColDef,
  GridColType,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { engineerOrAdmin, isAdmin } from '@src/services/auth';
import { UserRole } from '@src/services/auth/roles';
import { SeverityTypes } from '@src/types/Alerts';
import EventTimeCell from '../Table/EventTimeCell';
import SeverityCell from '../Table/SeverityCell';

interface ConfigColsParams {
  role: UserRole;
  rowSelectionModel: GridRowSelectionModel;
  handleEditClick: (id: GridRowId) => () => void;
  handleDeleteClick: (id: GridRowId) => () => void;
  alias: (text: string) => string;
}

const prepConfigColumns = ({
  role,
  rowSelectionModel,
  handleEditClick,
  handleDeleteClick,
  alias,
}: ConfigColsParams): GridColDef[] => [
  {
    field: 'alertName',
    headerName: alias('Alert name'),
    description: alias('Alert name'),
    type: 'string',
    width: 200,
    align: 'left',
    headerAlign: 'left',
    editable: true,
  },
  {
    field: 'topic',
    headerName: alias('Topic'),
    description: alias('Topic'),
    type: 'string',
    width: 300,
    editable: true,
  },
  {
    field: 'severity',
    headerName: alias('Severity'),
    description: alias('Severity'),
    width: 80,
    editable: true,
    headerAlign: 'center',
    align: 'center',
    type: 'singleSelect',
    valueOptions: [0, 1, 2],
    renderCell: ({ value }) => (
      <SeverityCell severity={SeverityTypes[value]} isAcked={false} />
    ),
  },
  {
    field: 'thresholdMin',
    headerName: alias('Min threshold'),
    description: alias('Min threshold'),
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    width: 110,
    editable: true,
  },
  {
    field: 'thresholdMax',
    headerName: alias('Max threshold'),
    description: alias('Max threshold'),
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    width: 110,
    editable: true,
  },
  {
    field: 'messageAboveMax',
    headerName: alias('Message above max'),
    description: alias('Message above max'),
    type: 'string',
    width: 250,
    editable: true,
  },
  {
    field: 'messageBelowMin',
    headerName: alias('Message below min'),
    description: alias('Message below min'),
    type: 'string',
    width: 250,
    editable: true,
  },
  {
    field: 'updatedAt',
    headerName: alias('Last Edit'),
    description: alias('Last Edit'),
    type: 'string',
    width: 200,
    editable: true,
    renderCell: ({ value }) => <EventTimeCell time={new Date(value)} />,
  },
  {
    field: 'isEquipmentFault',
    headerName: alias('Is Equipment Fault'),
    description: alias('Is Equipment Fault'),
    type: 'boolean',
    width: 150,
    editable: true,
    align: 'center',
    renderCell: ({ value }) =>
      value === true ? <FontAwesomeIcon icon={faCheck} size='lg' /> : null,
  },
  {
    field: 'enableDynamicMessage',
    headerName: alias('Dynamic Message Enabled'),
    description: alias('Message pulled from original-topic/message topic'),
    type: 'boolean',
    width: 150,
    editable: true,
    align: 'center',
    renderCell: ({ value }) =>
      value === true ? <FontAwesomeIcon icon={faCheck} size='lg' /> : null,
  },
  {
    field: 'timeoutActivateMs',
    headerName: alias('Timeout to active (ms)'),
    description: alias('Timeout to active (ms)'),
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    width: 120,
    editable: true,
  },
  {
    field: 'timeoutRemoveMs',
    headerName: alias('Timeout to remove (ms)'),
    description: alias('Timeout to remove (ms)'),
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    width: 120,
    editable: true,
  },
  ...(engineerOrAdmin(role) && !process.env.VITE_READ_ONLY_MODE
    ? [
        {
          field: 'actions',
          type: 'actions' as GridColType,
          headerName: alias('Actions'),
          description: alias('Actions'),
          width: 120,
          cellClassName: 'actions',
          getActions: ({ id }: GridRowParams<{ id: string }>) => [
            // biome-ignore lint/correctness/useJsxKeyInIterable: safe
            <GridActionsCellItem
              icon={<EditIcon />}
              label={alias('Edit')}
              className='textPrimary'
              onClick={handleEditClick(id)}
              color='inherit'
              disabled={rowSelectionModel.length > 1}
            />,
            // biome-ignore lint/correctness/useJsxKeyInIterable: safe
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label={alias('Delete')}
              onClick={handleDeleteClick(id)}
              color='inherit'
              disabled={!(rowSelectionModel.length < 2 && isAdmin(role))}
            />,
          ],
        } as GridBaseColDef,
      ]
    : []),
];

export default prepConfigColumns;
